import { useEffect, useMemo } from 'react';
import {
  LayoutGrid,
  Inbox,
  Sparkles,
  CheckSquare,
  CalendarDays,
  Calendar,
  Moon,
  CalendarCheck,
  Users,
  Target,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useTasks } from '../../hooks/useTasks';

const NAV_ITEMS = [
  { id: 'cockpit', label: 'Dashboard', icon: LayoutGrid },
  { id: 'inbox', label: 'Inbox', icon: Inbox, showBadge: true },
  { id: 'habits', label: 'Habits', icon: Sparkles },
  { id: 'tasks', label: 'Tasks', icon: CheckSquare },
  { id: 'schedule', label: 'Schedule', icon: CalendarDays },
  { id: 'meetings', label: 'Meetings', icon: Calendar },
  { id: 'eod', label: 'Daily Review', icon: Moon },
  { id: 'weeklyReview', label: 'Weekly Review', icon: CalendarCheck },
  { id: 'oneOnOne', label: '1:1s', icon: Users },
  { id: 'documents', label: 'Goals', icon: Target },
  { id: 'policies', label: 'Policies', icon: FileText },
];

const STORAGE_KEY = 'sidebar-collapsed';

export default function Sidebar({ activeTab, setActiveTab, taskManagementEnabled = true, isCollapsed, setIsCollapsed }) {
  const { logout } = useAuth();
  const { getInboxTasks } = useTasks();

  // Get inbox count for badge
  const inboxCount = useMemo(() => getInboxTasks().length, [getInboxTasks]);

  // Persist collapse state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isCollapsed));
  }, [isCollapsed]);

  // Filter nav items based on task management setting
  const visibleNavItems = NAV_ITEMS.filter(item => {
    if (!taskManagementEnabled) {
      return !['cockpit', 'inbox', 'tasks', 'schedule', 'eod', 'weeklyReview'].includes(item.id);
    }
    return true;
  });

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
  };

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      {/* Header with Logo and Toggle */}
      <div className="sidebar-header">
        <div className="sidebar-logo">
          {isCollapsed ? (
            <div className="sidebar-logo-icon" style={{ backgroundImage: 'url(/helm-icon.png)' }} aria-label="Helm" />
          ) : (
            <div className="sidebar-logo-full" style={{ backgroundImage: 'url(/helm-wordmark.png)' }} aria-label="Helm" />
          )}
        </div>
        <button
          className="sidebar-toggle"
          onClick={toggleCollapse}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        {visibleNavItems.map((item) => {
          const badge = item.showBadge && item.id === 'inbox' ? inboxCount : null;
          return (
            <button
              key={item.id}
              className={`sidebar-nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => handleNavClick(item.id)}
              data-tooltip={isCollapsed ? item.label : undefined}
              title={isCollapsed ? item.label : undefined}
            >
              <div className="relative">
                <item.icon className="sidebar-nav-icon" size={20} />
                {badge > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 bg-accent text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
              <span className="sidebar-nav-label">{item.label}</span>
              {!isCollapsed && badge > 0 && (
                <span className="ml-auto bg-accent/10 text-accent text-[10px] font-semibold px-1.5 py-0.5 rounded-full">
                  {badge > 99 ? '99+' : badge}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sidebar-footer">
        <button
          className={`sidebar-nav-item ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => handleNavClick('settings')}
          data-tooltip={isCollapsed ? 'Settings' : undefined}
          title={isCollapsed ? 'Settings' : undefined}
        >
          <Settings className="sidebar-nav-icon" size={20} />
          <span className="sidebar-nav-label">Settings</span>
        </button>

        <div className="sidebar-divider" />

        <button
          className="sidebar-nav-item"
          onClick={logout}
          data-tooltip={isCollapsed ? 'Sign Out' : undefined}
          title={isCollapsed ? 'Sign Out' : undefined}
        >
          <LogOut className="sidebar-nav-icon" size={20} />
          <span className="sidebar-nav-label">Sign Out</span>
        </button>
      </div>
    </aside>
  );
}
